import { Box, Button, Divider, Modal, Radio, Stack, Text, TextInput } from '@mantine/core';
import { useForm, schemaResolver } from '@mantine/form';
import { useState } from 'react';
import { z } from 'zod';
import { useValidateProviderReference } from 'src/financial/financial-data/hooks/useValidateProviderReference';
import { Logger } from 'src/utils/Logger';

const IDENTIFIER_CHARS = /^[a-zA-Z0-9-]+$/;
const IDENTIFIER_MAX_LENGTH = 36;

const optionalIdentifier = (label: string) =>
  z
    .string()
    .max(IDENTIFIER_MAX_LENGTH, `${label} must be 36 characters or less`)
    .regex(IDENTIFIER_CHARS, `${label} must contain only letters, numbers, and hyphens`)
    .optional()
    .or(z.literal(''));

const providerReferenceSchema = z
  .object({
    providerReference: optionalIdentifier('Provider reference number'),
    policyNumber: optionalIdentifier('Policy number'),
  })
  .refine(values => !!values.providerReference || !!values.policyNumber, {
    message: 'Enter a provider reference number or a policy number',
    path: ['providerReference'],
  });

type ProviderReferenceForm = z.infer<typeof providerReferenceSchema>;

type AssetUpdateChoiceModalProps = {
  opened: boolean;
  onClose: () => void;
  selectedMethod: 'connection' | 'manually';
  onMethodChange: (method: 'connection' | 'manually') => void;
  onContinue: () => void;
};

export const AssetUpdateChoiceModal = ({
  opened,
  onClose,
  selectedMethod,
  onMethodChange,
  onContinue,
}: AssetUpdateChoiceModalProps) => {
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="How would you like to add the data?"
      centered
      size="sm"
    >
      <Radio.Group
        value={selectedMethod}
        onChange={value => onMethodChange(value as 'connection' | 'manually')}
      >
        <Stack gap="lg" mb="xl">
          <Radio
            value="connection"
            label={
              <Box>
                <Text fw={500}>Connection</Text>
                <Text size="sm" c="dimmed">
                  Connect to the provider to automate data collection (usually updated daily)
                </Text>
              </Box>
            }
            styles={{ body: { alignItems: 'flex-start' }, inner: { marginTop: 4 } }}
          />

          <Divider />

          <Radio
            value="manually"
            label={
              <Box>
                <Text fw={500}>Manually</Text>
                <Text size="sm" c="dimmed">
                  Enter the data manually
                </Text>
              </Box>
            }
            styles={{ body: { alignItems: 'flex-start' }, inner: { marginTop: 4 } }}
          />
        </Stack>
      </Radio.Group>

      <Button fullWidth onClick={onContinue} size="md">
        Continue
      </Button>
    </Modal>
  );
};

type ProviderReferenceModalProps = {
  opened: boolean;
  onClose: () => void;
  onSubmit: (identifiers: { providerReference?: string; policyNumber?: string }) => void;
  isLoading?: boolean;
  clientId: string;
};

export const ProviderReferenceModal = ({
  opened,
  onClose,
  onSubmit,
  isLoading = false,
  clientId,
}: ProviderReferenceModalProps) => {
  const { validateProviderReference } = useValidateProviderReference(clientId);
  const [isValidating, setIsValidating] = useState(false);
  const form = useForm<ProviderReferenceForm>({
    initialValues: {
      providerReference: '',
      policyNumber: '',
    },
    validate: schemaResolver(providerReferenceSchema, { sync: true }),
    validateInputOnChange: true,
    validateInputOnBlur: true,
  });

  const handleClose = () => {
    if (!isLoading && !isValidating) {
      form.reset();
      onClose();
    }
  };

  const handleSubmit = async (values: ProviderReferenceForm) => {
    const providerReference = values.providerReference?.trim() || undefined;
    const policyNumber = values.policyNumber?.trim() || undefined;

    try {
      setIsValidating(true);

      const result = await validateProviderReference({
        providerReferenceNumber: providerReference,
        policyNumber,
      });
      if (result.data.is_duplicate) {
        const field = policyNumber ? 'policyNumber' : 'providerReference';
        const message = policyNumber
          ? 'This policy number is already in use for this provider reference'
          : 'This provider reference number is already in use';
        form.setFieldError(field, message);
        return;
      }
      if (result.data.has_ambiguous_feed_matches) {
        form.setFieldError(
          'providerReference',
          'Multiple wrappers match this provider reference. Add a policy number to disambiguate.',
        );
        return;
      }

      onSubmit({ providerReference, policyNumber });
      form.reset();
    } catch (error) {
      Logger.warn('Provider reference validation failed', { error });
      onSubmit({ providerReference, policyNumber });
      form.reset();
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <Modal opened={opened} onClose={handleClose} title="Connected data" centered size="sm">
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Text size="sm" mb="lg" c="dimmed">
          Enter a provider reference number, policy number, or both.
        </Text>
        <TextInput
          label="Provider reference number"
          {...form.getInputProps('providerReference')}
          disabled={isLoading || isValidating}
          mb="md"
        />
        <TextInput
          label="Policy number"
          {...form.getInputProps('policyNumber')}
          disabled={isLoading || isValidating}
          mb="xl"
        />
        <Button
          fullWidth
          type="submit"
          size="md"
          disabled={!form.isValid() || isLoading || isValidating}
          loading={isLoading || isValidating}
        >
          Continue
        </Button>
      </form>
    </Modal>
  );
};
